import { useMemo } from "react";

import type { FuseOptionKey, IFuseOptions } from "fuse.js";
import Fuse from "fuse.js";
import isNil from "lodash/isNil";

import { isNotNilNotEmpty } from "@/utils/shared.utils";

interface UseFuseSearchProps<T> {
  data: T[];
  searchKeys: FuseOptionKey<T>[];
  searchValue?: string;
  threshold?: IFuseOptions<T>["threshold"];
}

// threshold is manually set to 0.2 based on experimental observation of the search results
const DefaultThreshold = 0.2;

const useFuseSearch = <T>({
  data,
  searchKeys,
  searchValue,
  threshold,
}: UseFuseSearchProps<T>): T[] => {
  const fuseList = useMemo(() => {
    // For more information on fuzzy matching options,
    // see: https://www.fusejs.io/api/options.html#fuzzy-matching-options
    return new Fuse(data, {
      keys: searchKeys,
      threshold: isNil(threshold) ? DefaultThreshold : threshold,
    });
  }, [data, searchKeys, threshold]);

  if (!isNotNilNotEmpty(searchValue)) {
    return data;
  }

  const results = fuseList.search(searchValue).map((r) => r.item);

  return results;
};

export type { UseFuseSearchProps };
export default useFuseSearch;
